const CLEANUP_CONFIG = {
  ttl: 7 * 24 * 60 * 60 * 1000, // 7 days in milliseconds
  maxItems: 100, // Maximum number of AGG_ items to keep
  cleanupThreshold: 0.9, // Clean when localStorage is 90% full
};

function getLocalStorageStats() {
  let totalSize = 0;
  let aggItemCount = 0;

  for (let key in localStorage) {
    const item = localStorage.getItem(key);
    totalSize += (key.length + (item?.length || 0)) * 2; // chars to bytes (rough estimate)

    if (key.startsWith('AGG_')) {
      aggItemCount++;
    }
  }

  return {
    totalSize,
    aggItemCount,
    usageRatio: totalSize / (5 * 1024 * 1024), // Assume 5MB quota
  };
}

function cleanupExpiredItems() {
  const now = Date.now();
  const keysToRemove = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('AGG_')) {
      try {
        const item = localStorage.getItem(key);
        const data = JSON.parse(item);

        // Check if item has timestamp/expiry and is expired
        const expiry = data.expiry || (data.timestamp && data.timestamp + CLEANUP_CONFIG.ttl);
        if (expiry && now >= expiry) {
          keysToRemove.push(key);
        }
      } catch (e) {
        // Remove corrupted items
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach((key) => localStorage.removeItem(key));
  return keysToRemove.length;
}

function cleanupOldItems(aggressive = false) {
  const items = [];
  const now = Date.now();

  // Collect all AGG_ items with timestamps
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('AGG_')) {
      try {
        const item = localStorage.getItem(key);
        const data = JSON.parse(item);
        const timestamp = data.timestamp || data.lastAccessed || data.expiry || now;
        const size = (key.length + item.length) * 2;

        items.push({ key, timestamp, size });
      } catch (e) {
        // Remove corrupted items immediately
        console.error(`cleanupOldItems: removing corrupted item ${key}`, e);
        localStorage.removeItem(key);
      }
    }
  }

  // Sort by timestamp (oldest first)
  items.sort((a, b) => a.timestamp - b.timestamp);

  const removeCount = aggressive
    ? Math.floor(items.length / 2)
    : Math.max(0, items.length - CLEANUP_CONFIG.maxItems);

  let removedCount = 0;
  for (let i = 0; i < removeCount && i < items.length; i++) {
    localStorage.removeItem(items[i].key);
    removedCount++;
  }

  return removedCount;
}

// Collect every AGG_ record with its best-known timestamp, oldest first.
// Corrupted entries get timestamp 0 so they are evicted before valid ones.
function collectAggItems() {
  const items = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('AGG_')) continue;
    try {
      const data = JSON.parse(localStorage.getItem(key));
      items.push({ key, timestamp: data.timestamp || data.lastAccessed || data.expiry || 0 });
    } catch (e) {
      items.push({ key, timestamp: 0 });
    }
  }
  items.sort((a, b) => a.timestamp - b.timestamp);
  return items;
}

// Remove the oldest `fraction` (0..1) of AGG_ records. Returns how many were removed.
function removeOldestAgg(fraction) {
  const items = collectAggItems();
  const removeCount = Math.ceil(items.length * fraction);
  let removed = 0;
  for (let i = 0; i < removeCount && i < items.length; i++) {
    localStorage.removeItem(items[i].key);
    removed++;
  }
  return removed;
}

// Drop the oldest half of AGG_ records (first-line emergency relief).
function cleanupHalfOfAGGRecords() {
  const total = collectAggItems().length;
  const removed = removeOldestAgg(0.5);
  console.log(`Emergency cleanup: removed ${removed} oldest AGG_ records (50% of ${total} total)`);
  return removed;
}

// Drop every remaining AGG_ record (last-resort emergency relief).
function cleanupAllAggRecords() {
  const removed = removeOldestAgg(1);
  if (removed > 0) {
    console.log(`Emergency cleanup: removed all ${removed} remaining AGG_ records`);
  }
  return removed;
}

// Evict the least-recently-written scoped (`state-<bpId>-<profileId>-*`) combinations,
// keeping only the `maxCombinations` freshest profile/marketplace pairs. Lives here (not in
// scopedStorage.js) so every quota-exceeded path frees space across AGG_ AND scoped data.
function cleanupOldScopedData(maxCombinations = 20) {
  try {
    const keys = Object.keys(localStorage).filter((k) => k.startsWith('state-'));
    const combinations = {};

    keys.forEach((key) => {
      // Format: state-{businessProfileId}-{profileId}-{type}
      const match = key.match(/^state-(\d+)-(\d+)-/);
      if (!match) return;
      const combo = `${match[1]}-${match[2]}`;
      if (!combinations[combo]) combinations[combo] = { keys: [], timestamp: 0 };
      combinations[combo].keys.push(key);
      try {
        const data = JSON.parse(localStorage.getItem(key));
        // Freshness: storage-envelope writtenAt (current) or legacy inner _timestamp.
        const ts = data && (data.writtenAt || data._timestamp);
        if (ts && ts > combinations[combo].timestamp) {
          combinations[combo].timestamp = ts;
        }
      } catch (e) {
        // ignore parse errors
      }
    });

    // Newest combinations first; everything past the limit is removed.
    const sortedCombos = Object.entries(combinations).sort(
      (a, b) => b[1].timestamp - a[1].timestamp,
    );
    const combosToRemove = sortedCombos.slice(maxCombinations);

    let removedCount = 0;
    combosToRemove.forEach(([, data]) => {
      data.keys.forEach((key) => {
        localStorage.removeItem(key);
        removedCount++;
      });
    });

    if (removedCount > 0) {
      console.log(
        `[scopedStorage] Cleaned up ${removedCount} old keys, kept ${maxCombinations} most recent combinations`,
      );
    }
    return removedCount;
  } catch (error) {
    console.error('[scopedStorage] Error during cleanup:', error);
    return 0;
  }
}

function performCleanup(aggressive = false) {
  const stats = getLocalStorageStats();

  if (
    stats.usageRatio > CLEANUP_CONFIG.cleanupThreshold ||
    stats.aggItemCount > CLEANUP_CONFIG.maxItems
  ) {
    console.log('Performing localStorage cleanup...', { stats, aggressive });

    const expiredRemoved = cleanupExpiredItems();
    const oldRemoved = cleanupOldItems(aggressive);

    console.log(`Cleanup completed: ${expiredRemoved} expired, ${oldRemoved} old items removed`);

    return { expiredRemoved, oldRemoved };
  }

  return { expiredRemoved: 0, oldRemoved: 0 };
}

function trySetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

// Single quota-recovery path shared by every writer (AGG_ session store, scoped analytics
// state, grid sharing, dashboard). On QuotaExceededError it escalates — freeing expired and
// old data first, then everything disposable — and retries the write after each step, so a
// write of any kind can reclaim space across AGG_ AND scoped state, not just its own family.
function safeSetItem(key, value, serviceName = 'LocalStorageCleanup') {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (error.name !== 'QuotaExceededError') {
      console.error(`localStorage error in ${serviceName}:`, error);
      return false;
    }

    console.warn(`localStorage quota exceeded in ${serviceName}, performing emergency cleanup...`);

    // Pass 1 — gentle: expired AGG_, oldest half of AGG_, stale scoped combinations.
    cleanupExpiredItems();
    cleanupHalfOfAGGRecords();
    cleanupOldScopedData(20);
    if (trySetItem(key, value)) return true;

    // Pass 2 — aggressive: every remaining AGG_ record + all but the freshest scoped combos.
    cleanupAllAggRecords();
    cleanupOldScopedData(5);
    if (trySetItem(key, value)) return true;

    console.error(`Failed to store "${key}" in ${serviceName} even after emergency cleanup`);
    return false;
  }
}

// Extra maintenance tasks contributed by other modules (e.g. the storage service's TTL
// sweep). They run on every periodic tick — unconditionally, not gated by the fill
// threshold — so expired entries are reclaimed proactively. This is the single scheduler
// every storage layer hooks into instead of spinning up its own setInterval.
const cleanupTasks = [];
function registerCleanupTask(task) {
  if (typeof task === 'function' && !cleanupTasks.includes(task)) cleanupTasks.push(task);
}
function runCleanupTasks() {
  for (const task of cleanupTasks) {
    try {
      task();
    } catch (e) {
      console.error('[LocalStorageCleanup] cleanup task failed:', e);
    }
  }
}

function runPeriodicMaintenance() {
  performCleanup(false);
  runCleanupTasks();
}

let cleanupInterval = null;
function initializePeriodicCleanup() {
  if (cleanupInterval) return; // Already initialized

  // Run initial maintenance
  runPeriodicMaintenance();

  // Set up periodic maintenance every 5 minutes
  cleanupInterval = setInterval(runPeriodicMaintenance, 5 * 60 * 1000);

  // Maintenance on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      if (cleanupInterval) {
        clearInterval(cleanupInterval);
        cleanupInterval = null;
      }
      runPeriodicMaintenance();
    });
  }
}

// Export the cleanup service
export {
  // Main cleanup functions
  cleanupHalfOfAGGRecords,
  cleanupExpiredItems,
  cleanupOldScopedData,
  performCleanup,

  // Safe storage
  safeSetItem,

  // Statistics
  getLocalStorageStats,

  // Lifecycle
  initializePeriodicCleanup,
  registerCleanupTask,

  // Configuration
  CLEANUP_CONFIG,
};

// Auto-initialize periodic cleanup when service is imported
initializePeriodicCleanup();
